import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CopyResultButton } from "@/components/CopyResultButton";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { breadcrumbSchema, calculatorToolSchema } from "@/utils/structuredData";

export default function AnnuityPVCalculator() {
  const { t } = useTranslation();
  const [payment, setPayment] = useState("");
  const [rate, setRate] = useState("");
  const [periods, setPeriods] = useState("");
  const [annuityType, setAnnuityType] = useState("ordinary");
  const [result, setResult] = useState<number | null>(null);

  useLanguageAwareSEO([
    (lang) => breadcrumbSchema([
      { name: t('nav.home'), url: 'https://calclab.app' },
      { name: t('categories.finance'), url: 'https://calclab.app/finance' },
      { name: "Annuity PV Calculator", url: "https://calclab.app/calculator/annuity-pv" }
    ], lang),
    (lang) => calculatorToolSchema(
      "Annuity Present Value Calculator",
      "Calculate present value of regular payment streams. Support for ordinary annuity and annuity due calculations with custom interest rates.",
      "https://calclab.app/calculator/annuity-pv",
      lang
    )
  ]);

  const calculate = () => {
    const pmt = parseFloat(payment);
    const r = parseFloat(rate) / 100;
    const n = parseFloat(periods);

    if (isNaN(pmt) || isNaN(r) || isNaN(n) || pmt <= 0 || n <= 0 || r < 0) {
      toast.error("Please enter valid values");
      return;
    }

    let pv: number;

    if (annuityType === "ordinary") {
      // PV of ordinary annuity = PMT × [(1 - (1 + r)^-n) / r]
      pv = pmt * ((1 - Math.pow(1 + r, -n)) / r);
    } else {
      // PV of annuity due = PMT × [(1 - (1 + r)^-n) / r] × (1 + r)
      pv = pmt * ((1 - Math.pow(1 + r, -n)) / r) * (1 + r);
    }

    setResult(pv);
    toast.success("Present Value calculated!");
  };

  const reset = () => {
    setPayment("");
    setRate("");
    setPeriods("");
    setAnnuityType("ordinary");
    setResult(null);
  };

  return (
    <PageLayout>
      <Card>
        <CardHeader>
          <CardTitle>Annuity Present Value Calculator</CardTitle>
          <CardDescription>
            Calculate present value of regular payment streams
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="type">Annuity Type</Label>
            <Select value={annuityType} onValueChange={setAnnuityType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ordinary">Ordinary (End of Period)</SelectItem>
                <SelectItem value="due">Annuity Due (Beginning of Period)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="payment">Periodic Payment ($)</Label>
            <Input
              id="payment"
              type="number"
              placeholder="1000"
              value={payment}
              onChange={(e) => setPayment(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="rate">Interest Rate (% per period)</Label>
            <Input
              id="rate"
              type="number"
              placeholder="5"
              value={rate}
              onChange={(e) => setRate(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="periods">Number of Periods</Label>
            <Input
              id="periods"
              type="number"
              placeholder="10"
              value={periods}
              onChange={(e) => setPeriods(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <Button onClick={calculate}>Calculate</Button>
            <Button onClick={reset} variant="outline">Reset</Button>
          </div>
          {result !== null && (
            <>
              <div className="mt-4 p-4 bg-muted rounded-lg">
              <div className="flex items-center justify-between">
                <p className="text-lg break-all overflow-wrap-anywhere">
                  <strong>Present Value:</strong> ${result.toFixed(2)}
                </p>
                <CopyResultButton value={result.toFixed(2)} />
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                This is the current worth of {periods} payments of ${payment} each
              </p>
            </div>

            <CalculatorSuperCalcCTA config={calculatorCTAs["annuity-pv"]} trigger={result} />
            </>
          )}
        </CardContent>
      </Card>
    </PageLayout>
  );
}
